import type { Thread } from '../../lib/api';
import { Lock } from '../../lib/icons';

interface Props {
  thread: Thread;
  isNoteMode: boolean;
  signedLabel: string;
  onSwitchBack: () => void;
}

const labelStyle: React.CSSProperties = {
  fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
};

// Thin bar under the header — the only place (other than the pane tint) that
// tells the agent what mode they're in and who it's visible to. Blue for
// team-only threads, never amber — amber means "hidden from a client who is
// present in this thread," and team-only threads have no client to hide from.
export default function ThreadModeStrip({ thread, isNoteMode, signedLabel, onSwitchBack }: Props) {
  if (thread.type === 'client' && !isNoteMode) {
    return (
      <div style={{
        padding: '6px 20px', background: 'rgba(29,158,117,.10)', borderBottom: '1px solid rgba(29,158,117,.18)',
        display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
      }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--v2)' }} />
        <span style={{ ...labelStyle, color: 'var(--v2)' }}>MESSAGING CLIENT · {signedLabel}</span>
      </div>
    );
  }

  if (thread.type === 'client' && isNoteMode) {
    return (
      <div style={{
        padding: '6px 20px', background: 'rgba(239,159,39,.16)', borderBottom: '1px solid var(--chat-internal-border)',
        display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
      }}>
        <Lock size={11} strokeWidth={1.8} color="var(--note-label)" />
        <span style={{ ...labelStyle, color: 'var(--note-label)', flex: 1 }}>INTERNAL · CLIENT WON'T SEE THIS</span>
        <button
          onClick={onSwitchBack}
          style={{
            background: 'var(--note-border)', border: 'none', borderRadius: 10,
            padding: '2px 9px', fontSize: '0.66rem', fontWeight: 700, color: 'var(--note-label)', cursor: 'pointer',
          }}
        >
          Switch back
        </button>
      </div>
    );
  }

  if (thread.type === 'org') {
    return (
      <div style={{
        padding: '6px 20px', background: 'rgba(55,138,221,.12)', borderBottom: '1px solid rgba(55,138,221,.25)',
        display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
      }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--blue-soft)' }} />
        <span style={{ ...labelStyle, color: 'var(--blue-soft)', flex: 1 }}>TEAM ONLY · NO CLIENT CAN BE ADDED TO THIS THREAD</span>
        <span style={{ fontSize: '0.68rem', color: 'var(--shell-xs)' }}>You can't leave the org thread</span>
      </div>
    );
  }

  // internal / direct
  return (
    <div style={{
      padding: '6px 20px', background: 'rgba(55,138,221,.12)', borderBottom: '1px solid rgba(55,138,221,.25)',
      display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
    }}>
      <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--blue-soft)' }} />
      <span style={{ ...labelStyle, color: 'var(--blue-soft)' }}>TEAM ONLY · NO CLIENT IN THIS THREAD</span>
    </div>
  );
}
